// Shared presentational pieces for the tenant workflow-CUSTOMIZATION surfaces — used by both
// WorkflowSettingsCard (the SAM chat card) and the tenant Settings "Workflow" tab
// (WorkflowTenantSettingsSection). Extracted so the two surfaces render the same look/feel
// (toggle switches, rename affordance, badges) without copy-pasting the markup twice.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Button } from '@/components/UI/Button';

// Values a transition's authority can be granted to (tenant-scoped roles only — platform_admin /
// platform_support are cross-tenant and never assignable here).
export const ROLE_OPTIONS = ['tenant_admin', 'project_manager', 'sales_agent', 'office_admin', 'crew_member'];

export const CATEGORY_OPTIONS = ['standard', 'backward', 'skip', 'hold', 'resume', 'reopen', 'cancel', 'reject', 'corrective_loop'];

export const STAGE_PILL = 'bg-brand-bright/10 text-brand-bright dark:bg-brand-bright/15';
export const PROCESS_PILL = 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300';
export const CUSTOM_BADGE = 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
export const INACTIVE_BADGE = 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400';

export const FIELD_CLS = 'w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark';

export const humanize = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const Badge = ({ label, cls }: { label: string; cls: string }) => (
  <span className={cn('inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', cls)}>{label}</span>
);

// A small switch control — no dedicated Switch component exists in the codebase yet.
export const Toggle = ({ checked, disabled, title, onClick }: { checked: boolean; disabled?: boolean; title?: string; onClick: () => void }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    title={title}
    onClick={onClick}
    className={cn(
      'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
      checked ? 'bg-brand-dark' : 'bg-gray-300 dark:bg-white/15',
      disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
    )}
  >
    <span className={cn('inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform', checked ? 'translate-x-[18px]' : 'translate-x-1')} />
  </button>
);

// Inline rename affordance: click the label to reveal a text input (Cancel/Save pair). Disabled
// (no click target) when the item isn't `tenant_label_renamable`.
export const RenameLabel = ({
  code, label, renamable, busy, onRename, className,
}: {
  code: string; label: string; renamable: boolean; busy: boolean; className?: string;
  onRename: (code: string, newLabel: string) => void;
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(label);

  if (editing) {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={cn(FIELD_CLS, 'min-w-0 flex-1')}
        />
        <Button variant="ghost" size="sm" className="h-7 shrink-0 px-2" onClick={() => setEditing(false)}>
          {t('workflow.cancel', 'Cancel')}
        </Button>
        <Button
          size="sm" className="h-7 shrink-0 px-2" loading={busy}
          disabled={!value.trim() || value.trim() === label}
          onClick={() => { onRename(code, value.trim()); setEditing(false); }}
        >
          {t('workflowSettings.save', 'Save')}
        </Button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => { if (renamable) { setValue(label); setEditing(true); } }}
      disabled={!renamable}
      title={renamable ? t('workflowSettings.renameHint', 'Click to rename') : t('workflowSettings.notRenamable', 'This label cannot be renamed.')}
      className={cn(
        'truncate text-left font-medium text-ui-text',
        renamable ? 'cursor-pointer decoration-dotted hover:underline' : 'cursor-default',
        className,
      )}
    >
      {label}
    </button>
  );
};
